import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ChartConstants } from '@constants/pages/chart-constants';
import { Signal } from '@models/dashboard/signal';
import { Axis } from '@models/pages/interactive-data-analysis/interactive-data-analysis-signal-chart-properties';
import { ChartAxisSettings } from '@models/pages/user-defined-page/chart-axis-settings';
import { ChartSignalEntry, Colors } from '@models/pages/user-defined-page/chart-signal-entry';
import { SortingService } from '@services/administration/sorting/sorting.service';
import { ChartsConfigurationHelperService } from '@services/charts/helper/charts-configuration-helper.service';
import { cloneDeep } from 'lodash';

@Component({
    selector: 'scitis-line-widget-axis-edit',
    templateUrl: './line-widget-axis-edit.component.html',
    styleUrls: ['./line-widget-axis-edit.component.scss'],
    standalone: false
})
export class LineWidgetAxisEditComponent implements OnInit, OnChanges {

  @Input()
  axisSettings: ChartAxisSettings = new ChartAxisSettings();

  @Input()
  isSelectedAxis: boolean = false;

  @Input()
  usableSignals: Signal[] = [];

  @Input()
  usedSignalIds: number[] = [];

  @Output()
  axisSettingsChanged: EventEmitter<ChartAxisSettings> = new EventEmitter<ChartAxisSettings>();

  @Output()
  hasDeletedSignalsChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  selectedSignalIds: number[] = [];
  hasDeletedSignalsMap: Map<string, boolean> = new Map<string, boolean>();

  constructor(private sortingService: SortingService,
              public chartsConfigurationHelperService: ChartsConfigurationHelperService) {
    this.chartsConfigurationHelperService.hasDeletedSignals$.subscribe(hasDeletedSignals => {
      this.hasDeletedSignalsMap = hasDeletedSignals;
    });
  }

  public get isAxisNameNotSet(): boolean {
    return !this.axisSettings.axisYLabel || this.axisSettings.axisYLabel.isNullOrWhitespace();
  }

  public get isLeftAlignedAxis(): boolean {
    return this.axisSettings.position === Axis.LEFT;
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes?.axisSettings && this.usableSignals.length > 0) {
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(this.axisSettings.id, this.hasDeletedSignalsOnAxis()));
      this.emitSettingsChanged();
    }
    if(changes?.usableSignals && (this.axisSettings.signals?.length ?? 0) > 0) {
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(this.axisSettings.id, this.hasDeletedSignalsOnAxis()));
      this.emitSettingsChanged();
    }
  }

  ngOnInit(): void {
    this.axisSettings.signals ??= [];
    this.setSelectedSignals();
  }

  selectLeftAlignment() {
    this.axisSettings.position = Axis.LEFT;
    this.emitSettingsChanged();
  }

  selectRightAlignment() {
    this.axisSettings.position = Axis.RIGHT;
    this.emitSettingsChanged();
  }

  onAxisRangeChanged($event: number[]) {
    this.axisSettings.minY = $event[0];
    this.axisSettings.maxY = $event[1];
    this.emitSettingsChanged();
  }

  onRemoveSignalFromAxis(signal: ChartSignalEntry) {
    this.axisSettings.signals.splice(this.axisSettings.signals.findIndex(a => a.signalId === signal.signalId), 1);
    this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(this.axisSettings.id, this.hasDeletedSignalsOnAxis()));
    this.emitSettingsChanged();
  }

  private hasDeletedSignalsOnAxis() {
     return (this.axisSettings.signals ?? []).some(signal =>
       signal.signalId > 0 && !this.usableSignals.find(s => s.id == signal.signalId)
     );
  }

  onSignalSelected($event: Signal | Signal[]) {
    const signalsBackup = cloneDeep(this.axisSettings.signals);
    this.axisSettings.signals = [];
    if (!$event) {
      this.emitSettingsChanged();
      return;
    }
    const signals: Signal[] = Array.isArray($event) ? $event : [$event];
    for (const signal of signals) {
      const isSelectedSignal = this.selectedSignalIds.find(signalId => signalId === signal?.id);
      const chartSignal: ChartSignalEntry = new ChartSignalEntry(signal);
      chartSignal.axisId = this.axisSettings.id;
      if (!isSelectedSignal) {
        chartSignal.colors = new Colors();
        chartSignal.colors.main = this.chartsConfigurationHelperService.convertHexColorToNumber(signal.signalColor);
      } else {
        const selectedSignalColors = signalsBackup.find(s => s.signalId === signal?.id)?.colors;
        chartSignal.colors = selectedSignalColors ? selectedSignalColors : new Colors();
        chartSignal.colors.main = selectedSignalColors?.main || this.chartsConfigurationHelperService.convertHexColorToNumber(signal.signalColor);
      }
      this.axisSettings.signals = [...this.axisSettings.signals, chartSignal];
      this.chartsConfigurationHelperService.hasDeletedSignals$.next(this.hasDeletedSignalsMap.set(this.axisSettings.id, this.hasDeletedSignalsOnAxis()));
    }
    this.emitSettingsChanged();
  }

  getSignalOriginalColor(signal: ChartSignalEntry) {
    if (!signal || !signal.signalId) {
      return ChartConstants.DEFAULT_LINE_COLOR;
    }
    const machineSignal = this.usableSignals.find(s => s.id === signal.signalId);
    if (!machineSignal || !machineSignal.signalColor) {
      return ChartConstants.DEFAULT_LINE_COLOR;
    }
    return machineSignal.signalColor;
  }

  onSignalColorChanged(color: string, signal: ChartSignalEntry) {
    if (color && signal) {
      this.axisSettings.signals.map((s: ChartSignalEntry) => {
        if (s.signalId === signal.signalId) {
          const normalizedColor = this.chartsConfigurationHelperService.normalizeColorToHex(color);
          s.colors.main = this.chartsConfigurationHelperService.convertHexColorToNumber(normalizedColor);
        }
      });
    }
  }

  onAxisLabelChange() {
    this.emitSettingsChanged();
  }

  sortByLabel(signals: ChartSignalEntry[]): ChartSignalEntry[] {
    return this.sortingService.sortArrayByProperty(signals, 'label', true);
  }

  hasSignalBeenDeleted(signal: ChartSignalEntry): boolean {
    return !this.usableSignals.find(s => s.id == signal.signalId);
  }

  private emitSettingsChanged() {
    this.setSelectedSignals();
    this.hasDeletedSignalsChanged.emit(this.hasDeletedSignalsOnAxis());
    this.axisSettingsChanged.emit(this.axisSettings);
  }

  private setSelectedSignals() {
    if (this.axisSettings && this.axisSettings.signals) {
      const selectedSignals = this.axisSettings.signals;
      const selectedSignalIds: number[] = this.chartsConfigurationHelperService.getSelectedSignalIdsById(selectedSignals, this.usableSignals);
      this.selectedSignalIds = [...selectedSignalIds];
    }
  }
}
